// Filename: JobSharingManager.ts

export interface JobSharingLinks {
    url: string;
    title: string;
    email: string;
    linkedin: string;
    facebook: string;
}

const initializedDocuments = new WeakSet<Document>();
const PRIVATE_QUERY_KEYS = new Set([
    'versionid',
    'pagekey',
    'pageslug',
    'jobid',
    'employerid',
]);

function cleanTitle(value: unknown): string {
    return String(value ?? '')
        .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
        .replace(/<[^>]+>/g, '')
        .replace(/[\u0000-\u001f\u007f]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 500);
}

function baseUrlFor(runtimeWindow?: Window | null): URL {
    const candidate = runtimeWindow?.location?.href;
    if (candidate) {
        try {
            return new URL(candidate);
        } catch {
            // Use a non-routable base for unit tests and malformed locations.
        }
    }
    return new URL('https://fernain.invalid/');
}

/**
 * Resolve only the supplied canonical job destination. The current page URL
 * is used for its origin, never for its path, search, or fragment.
 */
export function sanitizeShareUrl(value: unknown, baseUrl = 'https://fernain.invalid/'): string | null {
    const text = String(value ?? '').trim();
    if (!text || text.startsWith('#') || text.startsWith('//')) return null;

    let url: URL;
    try {
        url = new URL(text, baseUrl);
    } catch {
        return null;
    }
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    if (url.username || url.password || /^\/acp\/partners\//i.test(url.pathname)) return null;

    for (const key of Array.from(url.searchParams.keys())) {
        if (PRIVATE_QUERY_KEYS.has(key.toLowerCase())) url.searchParams.delete(key);
    }
    url.hash = '';
    return url.toString().slice(0, 2000);
}

export function buildJobSharingLinks(
    rawUrl: unknown,
    rawTitle: unknown,
    baseUrl = 'https://fernain.invalid/'
): JobSharingLinks | null {
    const url = sanitizeShareUrl(rawUrl, baseUrl);
    const title = cleanTitle(rawTitle);
    if (!url || !title) return null;

    return {
        url,
        title,
        email: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${title}\n${url}`)}`,
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    };
}

function closestElement(target: EventTarget | null, selector: string): HTMLElement | null {
    if (!target || typeof target !== 'object') return null;
    const candidate = target as { closest?: (value: string) => Element | null };
    if (typeof candidate.closest !== 'function') return null;
    return candidate.closest(selector) as HTMLElement | null;
}

function setShareLink(
    sharing: HTMLElement,
    option: 'email' | 'linkedin' | 'facebook',
    href: string
): void {
    const link = sharing.querySelector<HTMLAnchorElement>(`[data-fj-job-share-option="${option}"]`);
    if (link) link.setAttribute('href', href);
}

export function hydrateJobSharingLinks(
    root: ParentNode = document,
    runtimeWindow?: Window | null
): void {
    const baseUrl = baseUrlFor(runtimeWindow ?? (root as Document).defaultView);
    root.querySelectorAll<HTMLElement>('[data-fj-job-sharing]').forEach((sharing) => {
        const links = buildJobSharingLinks(
            sharing.dataset.fjJobShareUrl,
            sharing.dataset.fjJobShareTitle,
            baseUrl.href
        );
        if (!links) return;
        setShareLink(sharing, 'email', links.email);
        setShareLink(sharing, 'linkedin', links.linkedin);
        setShareLink(sharing, 'facebook', links.facebook);
    });
}

async function copyText(value: string, runtimeWindow: Window | null): Promise<boolean> {
    const clipboard = runtimeWindow?.navigator?.clipboard;
    if (clipboard && typeof clipboard.writeText === 'function') {
        try {
            await clipboard.writeText(value);
            return true;
        } catch {
            // Fall back to the editing command where supported.
        }
    }

    const documentRoot = runtimeWindow?.document;
    if (!documentRoot || typeof documentRoot.createElement !== 'function') return false;
    const textarea = documentRoot.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.setAttribute('aria-hidden', 'true');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    documentRoot.body?.appendChild(textarea);
    textarea.select();
    let copied = false;
    try {
        copied = typeof documentRoot.execCommand === 'function'
            && documentRoot.execCommand('copy');
    } catch {
        copied = false;
    }
    textarea.remove();
    return copied;
}

function setCopyStatus(sharing: HTMLElement, message: string): void {
    const status = sharing.querySelector<HTMLElement>('[data-fj-job-share-status]');
    if (!status) return;
    status.textContent = message;
}

export function initializeJobSharing(root?: Document): void {
    const documentRoot = root ?? (typeof document !== 'undefined' ? document : null);
    if (!documentRoot || initializedDocuments.has(documentRoot)) return;
    initializedDocuments.add(documentRoot);
    const runtimeWindow = documentRoot.defaultView ?? (typeof window !== 'undefined' ? window : null);

    hydrateJobSharingLinks(documentRoot, runtimeWindow);

    documentRoot.addEventListener('click', (event) => {
        const target = event.target;
        const sharing = closestElement(target, '[data-fj-job-sharing]');
        if (!sharing) return;

        const option = closestElement(target, '[data-fj-job-share-option]')
            ?.getAttribute('data-fj-job-share-option') as 'email' | 'linkedin' | 'facebook' | null;
        if (option === 'email' || option === 'linkedin' || option === 'facebook') {
            const links = buildJobSharingLinks(
                sharing.dataset.fjJobShareUrl,
                sharing.dataset.fjJobShareTitle,
                baseUrlFor(runtimeWindow).href
            );
            if (links) setShareLink(sharing, option, links[option]);
            return;
        }

        if (!closestElement(target, '[data-fj-job-share-copy]')) return;
        event.preventDefault();
        const links = buildJobSharingLinks(
            sharing.dataset.fjJobShareUrl,
            sharing.dataset.fjJobShareTitle,
            baseUrlFor(runtimeWindow).href
        );
        if (!links) {
            setCopyStatus(sharing, 'This job link is unavailable.');
            return;
        }
        void copyText(links.url, runtimeWindow).then((copied) => {
            setCopyStatus(sharing, copied ? 'Link copied.' : 'Unable to copy the link.');
        });
    });
}

export default initializeJobSharing;
